import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import type { StudentProfile } from '../types/portal';

// Muss mit v_grace_days in enforce_payment_access() (DB-Migration) übereinstimmen.
export const PAYMENT_GRACE_DAYS = 7;
const WARN_DAYS  = 7;

function todayUtc(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
}

function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

// Eine Zahlung sperrt den Zugang erst, wenn sie unbezahlt UND länger als die
// Karenzzeit überfällig ist. Der Status "overdue" allein (wird vom Cron schon
// am Tag nach Fälligkeit gesetzt) dient nur der Anzeige.
export function isBlockingPayment(p: { status: string; due_date: string }): boolean {
  return (
    (p.status === 'open' || p.status === 'overdue') &&
    p.due_date < addDays(todayUtc(), -PAYMENT_GRACE_DAYS)
  );
}

type AccessReason = 'admin' | 'manual' | 'access_until' | 'active_plan' | 'none';

export interface StudentAccess {
  hasAccess: boolean;
  accessReason: AccessReason;
  hasPendingPayment: boolean;
  nextDueDate: string | null;
  planId: string | null;
  isWarning: boolean;
  loading: boolean;
  error: string | null;
}

interface UseStudentAccessArgs {
  user: User | null;
  profile: StudentProfile | null;
  authLoading: boolean;
}

const deniedAccessState: StudentAccess = {
  hasAccess: false,
  accessReason: 'none',
  hasPendingPayment: false,
  nextDueDate: null,
  planId: null,
  isWarning: false,
  loading: false,
  error: null,
};

export const useStudentAccess = ({ user, profile, authLoading }: UseStudentAccessArgs): StudentAccess => {
  const [access, setAccess] = useState<StudentAccess>({
    hasAccess: false,
    accessReason: 'none',
    hasPendingPayment: false,
    nextDueDate: null,
    planId: null,
    isWarning: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (authLoading) return;

    if (!user || !profile) {
      setAccess(deniedAccessState);
      return;
    }

    // Admin bypasses all payment checks immediately, no DB query needed
    if (profile.role === 'admin') {
      setAccess({ hasAccess: true, accessReason: 'admin', hasPendingPayment: false, nextDueDate: null, planId: null, isWarning: false, loading: false, error: null });
      return;
    }

    // Freier Zugang auf Kontoebene (Test-/Familienkonten): dauerhafter
    // Zugang ohne Zahlungsplan und ohne Zahlungsprüfung.
    if (profile.free_access) {
      setAccess({ hasAccess: true, accessReason: 'manual', hasPendingPayment: false, nextDueDate: null, planId: null, isWarning: false, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setAccess(prev => ({ ...prev, loading: true, error: null }));

    const check = async () => {
      try {
        const today = todayUtc();
        const warnCutoff = addDays(today, WARN_DAYS);

        const { data: plans, error: plansError } = await supabase
          .from('student_payment_plans')
          .select('id, status, manual_override, access_until')
          .eq('student_id', user.id)
          .neq('status', 'cancelled');

        if (cancelled) return;
        if (plansError) throw plansError;

        if (!plans || plans.length === 0) {
          setAccess({ hasAccess: false, accessReason: 'none', hasPendingPayment: false, nextDueDate: null, planId: null, isWarning: false, loading: false, error: null });
          return;
        }

        const manualPlan = plans.find(p => p.manual_override);
        if (manualPlan) {
          setAccess({ hasAccess: true, accessReason: 'manual', hasPendingPayment: false, nextDueDate: null, planId: null, isWarning: false, loading: false, error: null });
          return;
        }

        const accessUntilPlan = plans.find(p => p.access_until && p.access_until >= today);
        if (accessUntilPlan) {
          setAccess({ hasAccess: true, accessReason: 'access_until', hasPendingPayment: false, nextDueDate: accessUntilPlan.access_until, planId: null, isWarning: false, loading: false, error: null });
          return;
        }

        // "completed" = vollständig bezahlt → gewährt weiterhin Zugang.
        const accessPlanIds = plans
          .filter(p => p.status === 'active' || p.status === 'completed')
          .map(p => p.id);
        // Checkout ist nur für aktive Pläne möglich.
        const activePlanId = plans.find(p => p.status === 'active')?.id ?? null;

        if (accessPlanIds.length > 0) {
          const { data: payments, error: paymentsError } = await supabase
            .from('student_payments')
            .select('status, due_date')
            .in('plan_id', accessPlanIds)
            .neq('status', 'cancelled');

          if (cancelled) return;
          if (paymentsError) throw paymentsError;

          const hasBlocking = payments?.some(isBlockingPayment) ?? false;

          const hasPending = payments?.some(p => p.status === 'open' || p.status === 'overdue') ?? false;

          const nextUnpaid = payments
            ?.filter(p => p.status === 'open' || p.status === 'overdue')
            .sort((a, b) => a.due_date.localeCompare(b.due_date))[0];

          const isWarning = !hasBlocking && (payments?.some(p =>
            (p.status === 'open' || p.status === 'overdue') && p.due_date <= warnCutoff
          ) ?? false);

          if (!hasBlocking) {
            setAccess({
              hasAccess: true,
              accessReason: 'active_plan',
              hasPendingPayment: hasPending,
              nextDueDate: nextUnpaid?.due_date ?? null,
              planId: activePlanId,
              isWarning,
              loading: false,
              error: null,
            });
            return;
          }

          setAccess({
            hasAccess: false,
            accessReason: 'none',
            hasPendingPayment: true,
            nextDueDate: nextUnpaid?.due_date ?? null,
            planId: activePlanId,
            isWarning: false,
            loading: false,
            error: null,
          });
          return;
        }

        // Nur pausierte Pläne übrig: Zugang gesperrt, aber kein Self-Checkout —
        // der Schüler soll sich an den Lehrer wenden (planId bleibt null).
        const hasPending = plans.some(p => p.status === 'paused');

        setAccess({ hasAccess: false, accessReason: 'none', hasPendingPayment: hasPending, nextDueDate: null, planId: null, isWarning: false, loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        if (import.meta.env.DEV) console.error('useStudentAccess error:', err);
        setAccess({ ...deniedAccessState, error: 'Zugriffsprüfung fehlgeschlagen.' });
      }
    };

    check();
    return () => { cancelled = true; };
  }, [user, profile, authLoading]);

  return access;
};
